const DEFAULT_TOOLS = ['dashboard', 'profile', 'version', 'fleetdata'];

const EDIT_ALL_PERMISSION_BY_TOOL = {
  editor: 'editor.edit_all_template',
  publisher: 'global.edit_all_jobs',
  inspector: null,
  report: 'global.edit_all_jobs',
};

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.map((item) => String(item).trim());
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const normalizeToolName = (tool) =>
  String(tool || '')
    .trim()
    .toLowerCase();

const extractPermissions = (value) =>
  normalizeList(value).filter((entry) => entry.includes('.'));

const isPowerAdmin = (user) => {
  if (!user) return false;

  const role = String(user.role || '')
    .trim()
    .toLowerCase();
  const privil = parseInt(user.privil, 10);

  return (!isNaN(privil) && privil === 1) || role === 'power admin';
};

const hasRole = (user, expectedRoles = []) => {
  const allowedRoles = normalizeList(expectedRoles).map((role) =>
    role.toLowerCase()
  );

  if (allowedRoles.length < 1) return true;
  return allowedRoles.includes(String(user?.role || '').toLowerCase());
};

const getCurrentRole = (state) => {
  const currentUserRole = String(state.currentUser?.role || '').trim();
  if (!currentUserRole) return null;

  return (
    (state.userRoles || []).find(
      (role) =>
        String(role.rolename || '').toLowerCase() ===
        currentUserRole.toLowerCase()
    ) || null
  );
};

const getCurrentUserPermissions = (state) => {
  const currentPermissions = normalizeList(state.currentUserAllowedPermissions);
  if (currentPermissions.length > 0) {
    return Array.from(new Set(currentPermissions));
  }

  const role = getCurrentRole(state);
  const rolePermissions = extractPermissions(role?.permissions);
  const roleToolPermissions = extractPermissions(role?.toollist);
  const userPermissions = extractPermissions(state.currentUser?.permissions);
  const userToolPermissions = extractPermissions(state.currentUser?.toollist);

  return Array.from(
    new Set([
      ...rolePermissions,
      ...roleToolPermissions,
      ...userPermissions,
      ...userToolPermissions,
    ])
  );
};

const includesPermission = (permissions, permission) => {
  const permissionKey = String(permission || '').trim();
  if (!permissionKey) return false;

  return permissions.includes(permissionKey);
};

const hasPermission = (state, permission) => {
  if (isPowerAdmin(state.currentUser)) return true;

  const permissions = getCurrentUserPermissions(state);
  return includesPermission(permissions, permission);
};

const hasAnyPermission = (state, requiredPermissions = []) => {
  if (isPowerAdmin(state.currentUser)) return true;

  const requestedPermissions = normalizeList(requiredPermissions);
  if (requestedPermissions.length < 1) return true;

  const permissions = getCurrentUserPermissions(state);
  return requestedPermissions.some((permission) =>
    includesPermission(permissions, permission)
  );
};

const hasAllPermissions = (state, requiredPermissions = []) => {
  if (isPowerAdmin(state.currentUser)) return true;

  const requestedPermissions = normalizeList(requiredPermissions);
  if (requestedPermissions.length < 1) return true;

  const permissions = getCurrentUserPermissions(state);
  return requestedPermissions.every((permission) =>
    includesPermission(permissions, permission)
  );
};

const isJobOwner = (user, job) => {
  if (!user || !job || !job.prodCreatedByUser) return false;
  return String(job.prodCreatedByUser._id || '') === String(user._id || '');
};

const getEditPermissionForTool = (tool) => {
  switch (normalizeToolName(tool)) {
    case 'editor':
      return 'editor.edit_all_template';
    case 'publisher':
      return 'publisher.edit_visible_jobs';
    case 'inspector':
      return 'inspector.edit_visible_jobs';
    case 'report':
      return 'report.edit_visible_jobs';
    default:
      return null;
  }
};

const canAccessTool = (state, tool) => {
  if (isPowerAdmin(state.currentUser)) return true;

  const requestedTool = normalizeToolName(tool);
  if (!requestedTool) return false;

  if (DEFAULT_TOOLS.includes(requestedTool)) return true;

  const allowedTools = normalizeList(state.currentUserAllowedTools).map(
    normalizeToolName
  );

  return allowedTools.includes(requestedTool);
};

const canEditJob = (
  state,
  { job, tool, allowLegacyNoPermissions = false } = {}
) => {
  if (isPowerAdmin(state.currentUser)) return true;
  if (!job) return false;
  if (isJobOwner(state.currentUser, job)) return true;

  const permissions = getCurrentUserPermissions(state);
  if (allowLegacyNoPermissions && permissions.length === 0) return true;

  const editPermission = getEditPermissionForTool(tool);
  if (!editPermission) {
    console.warn(
      `[Auth] Unrecognized tool context: "${tool}". Denying access.`
    );
    return false;
  }

  const normalizedTool = normalizeToolName(tool);
  const editAllPermission = EDIT_ALL_PERMISSION_BY_TOOL[normalizedTool];

  const hasToolEditPermission = includesPermission(permissions, editPermission);
  const hasToolEditAllPermission = editAllPermission
    ? includesPermission(permissions, editAllPermission)
    : false;

  return hasToolEditPermission || hasToolEditAllPermission;
};

const canMutateTask = (
  state,
  {
    job,
    tool,
    isCompleted = false,
    isReadOnly = false,
    allowLegacyNoPermissions = false,
  } = {}
) => {
  if (isCompleted || isReadOnly) return false;

  return canEditJob(state, {
    job,
    tool,
    allowLegacyNoPermissions,
  });
};

export {
  DEFAULT_TOOLS,
  EDIT_ALL_PERMISSION_BY_TOOL,
  canAccessTool,
  canEditJob,
  canMutateTask,
  getEditPermissionForTool,
  getCurrentUserPermissions,
  hasAllPermissions,
  hasAnyPermission,
  hasPermission,
  hasRole,
  isJobOwner,
  isPowerAdmin,
  normalizeList,
};
